import { SkeletonProps } from 'antd'
import React, { FunctionComponent, ReactNode } from 'react'
import Blur from '@/components/common/blur'
import KolDetailStatistic from '@/components/kol/common/kol-detail-statistic'
import { Percentage } from '@/components/kol/detail/audience-analyses/audience-analysis'
import useAudienceAnalyze from '@/components/kol/hooks/use-audience-analyze'
import { useIntl } from '@/i18n/hooks/use-intl'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { PlatformCode } from '@/utils/convert-platform'

interface AudienceMainAgeProps {
  statisticLoading: boolean | SkeletonProps
  platformCode: PlatformCode
  isFetchingKolInfo: boolean
  isKolInfoLocked: boolean
  kolInfo?: KolSchemaModel
  showMockData?: boolean
}

const AudienceMainAgeStatistic: FunctionComponent<AudienceMainAgeProps> = ({
  statisticLoading,
  kolInfo,
  platformCode,
  isFetchingKolInfo,
  isKolInfoLocked,
  showMockData,
}) => {
  const { formatMessage, formatNumber } = useIntl()
  const { audienceAnalyze, isFetching: isFetchingAudienceAnalyze } =
    useAudienceAnalyze({
      kolId: kolInfo?.id,
      kolDcIdHashed: kolInfo?.dcIdHashed,
      platform: platformCode,
      isFetchingKolInfo,
      isLocked: isKolInfoLocked,
    })
  const hiddenData = isKolInfoLocked && !showMockData

  return (
    <KolDetailStatistic
      loading={statisticLoading || isFetchingAudienceAnalyze}
      suffix={((): ReactNode | undefined => {
        if (audienceAnalyze?.mainAge?.weight === undefined) {
          return undefined
        }

        return (
          <Percentage>
            <Blur activate={hiddenData}>
              {formatNumber(audienceAnalyze.mainAge.weight, {
                isPercentageDisplay: true,
              })}
            </Blur>
          </Percentage>
        )
      })()}
      title={formatMessage({ id: 'kol:audience_main_age' })}
      value={
        audienceAnalyze?.mainAge?.code ? (
          <Blur activate={hiddenData}>
            {formatMessage(
              { id: 'kol:audience_analysis_age_range' },
              { ageRange: audienceAnalyze.mainAge.code },
            )}
          </Blur>
        ) : null
      }
    />
  )
}

export default AudienceMainAgeStatistic
